import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Tabs,
  Tab,
  TextField,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Chip,
} from '@mui/material';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  Notifications as NotificationsIcon,
  VpnKey as VpnKeyIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import { updateUser } from '../store/slices/authSlice';
import api from '../services/api';
import { getAutomationRules, createAutomationRule, toggleAutomationRule } from '../services/automationService';
import { getCurrentOrganization, updateOrganization } from '../services/organizationService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const SettingsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const [tabValue, setTabValue] = useState(0);
  const [automationRules, setAutomationRules] = useState<any[]>([]);
  const [organization, setOrganization] = useState<any>(null);
  const [openAutomationDialog, setOpenAutomationDialog] = useState(false);

  // Profile form
  const [profileForm, setProfileForm] = useState({
    firstName: '', lastName: '', preferredLanguage: 'en',
  });
  const [profileSaved, setProfileSaved] = useState(false);

  useEffect(() => {
    if (currentUser) {
      setProfileForm({
        firstName: currentUser.firstName || '',
        lastName: currentUser.lastName || '',
        preferredLanguage: (currentUser as any).preferredLanguage || 'en',
      });
    }
  }, [currentUser]);

  const handleSaveProfile = async () => {
    try {
      await api.put(`/users/${currentUser?.id}`, {
        firstName: profileForm.firstName,
        lastName: profileForm.lastName,
        preferredLanguage: profileForm.preferredLanguage,
      });
      // Update Redux state + localStorage
      dispatch(updateUser({
        firstName: profileForm.firstName,
        lastName: profileForm.lastName,
      }));
      setProfileSaved(true);
      setTimeout(() => setProfileSaved(false), 3000);
    } catch (error) {
      console.error('Failed to save profile', error);
      alert('Failed to save profile');
    }
  };

  // Notification preferences
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [pushNotifications, setPushNotifications] = useState(true);
  const [taskAssignedNotif, setTaskAssignedNotif] = useState(true);
  const [taskDueNotif, setTaskDueNotif] = useState(true);
  const [commentNotif, setCommentNotif] = useState(true);

  // New automation rule
  const [newRuleName, setNewRuleName] = useState('');
  const [newRuleTrigger, setNewRuleTrigger] = useState('task_created');
  const [newRuleAction, setNewRuleAction] = useState('send_notification');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [rules, org] = await Promise.all([
        getAutomationRules(),
        getCurrentOrganization(),
      ]);
      setAutomationRules(rules);
      setOrganization(org);
    } catch (error) {
      console.error('Failed to load settings', error);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleToggleAutomationRule = async (ruleId: string, currentState: boolean) => {
    try {
      await toggleAutomationRule(ruleId, !currentState);
      loadData();
    } catch (error) {
      console.error('Failed to toggle rule', error);
    }
  };

  const handleCreateAutomationRule = async () => {
    try {
      await createAutomationRule({
        name: newRuleName,
        description: `Auto-created rule: ${newRuleName}`,
        triggerType: newRuleTrigger,
      });
      setOpenAutomationDialog(false);
      setNewRuleName('');
      loadData();
    } catch (error) {
      console.error('Failed to create rule', error);
    }
  };

  const handleUpdateOrganization = async () => {
    if (!organization) return;
    try {
      await updateOrganization(organization.id, {
        name: organization.name,
      });
      alert('Organization updated successfully');
    } catch (error) {
      console.error('Failed to update organization', error);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Settings
      </Typography>

      <Paper sx={{ mt: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="settings tabs">
          <Tab icon={<PersonIcon />} label="Profile" />
          <Tab icon={<BusinessIcon />} label="Organization" />
          <Tab icon={<NotificationsIcon />} label="Notifications" />
          <Tab icon={<VpnKeyIcon />} label="Automation" />
        </Tabs>

        {/* Profile Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Profile Settings
            </Typography>
            {profileSaved && (
              <Box sx={{ mb: 2, p: 1, bgcolor: 'success.light', borderRadius: 1 }}>
                <Typography variant="body2" color="success.dark">Profile saved successfully!</Typography>
              </Box>
            )}
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="First Name" value={profileForm.firstName}
                  onChange={e => setProfileForm({ ...profileForm, firstName: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Last Name" value={profileForm.lastName}
                  onChange={e => setProfileForm({ ...profileForm, lastName: e.target.value })} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Email" type="email"
                  value={currentUser?.email || ''} disabled />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Language" select
                  value={profileForm.preferredLanguage}
                  onChange={e => setProfileForm({ ...profileForm, preferredLanguage: e.target.value })}>
                  <MenuItem value="en">English</MenuItem>
                  <MenuItem value="mk">Македонски</MenuItem>
                  <MenuItem value="sr">Српски</MenuItem>
                  <MenuItem value="tr">Türkçe</MenuItem>
                  <MenuItem value="sq">Shqip</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <Button variant="contained" onClick={handleSaveProfile}>
                  Save Changes
                </Button>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>

        {/* Organization Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Organization Settings
            </Typography>
            {organization && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Organization Name"
                    value={organization.name || ''}
                    onChange={(e) => setOrganization({ ...organization, name: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Subscription: {organization.subscriptionTier || 'Free'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Created: {organization.createdAt ? new Date(organization.createdAt).toLocaleDateString() : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Button variant="contained" onClick={handleUpdateOrganization}>
                    Update Organization
                  </Button>
                </Grid>
              </Grid>
            )}
          </Box>
        </TabPanel>

        {/* Notifications Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Notification Preferences
            </Typography>
            <List>
              <ListItem>
                <ListItemText
                  primary="Email Notifications"
                  secondary="Receive notifications via email"
                />
                <ListItemSecondaryAction>
                  <Switch
                    edge="end"
                    checked={emailNotifications}
                    onChange={(e) => setEmailNotifications(e.target.checked)}
                  />
                </ListItemSecondaryAction>
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="Push Notifications"
                  secondary="Receive push notifications in browser"
                />
                <ListItemSecondaryAction>
                  <Switch
                    edge="end"
                    checked={pushNotifications}
                    onChange={(e) => setPushNotifications(e.target.checked)}
                  />
                </ListItemSecondaryAction>
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="Task Assignments"
                  secondary="Notify when you're assigned to a task"
                />
                <ListItemSecondaryAction>
                  <Switch
                    edge="end"
                    checked={taskAssignedNotif}
                    onChange={(e) => setTaskAssignedNotif(e.target.checked)}
                  />
                </ListItemSecondaryAction>
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="Due Date Reminders"
                  secondary="Notify when tasks are due soon"
                />
                <ListItemSecondaryAction>
                  <Switch
                    edge="end"
                    checked={taskDueNotif}
                    onChange={(e) => setTaskDueNotif(e.target.checked)}
                  />
                </ListItemSecondaryAction>
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="Comments & Mentions"
                  secondary="Notify when someone mentions you in a comment"
                />
                <ListItemSecondaryAction>
                  <Switch
                    edge="end"
                    checked={commentNotif}
                    onChange={(e) => setCommentNotif(e.target.checked)}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            </List>
            <Box sx={{ mt: 3 }}>
              <Button variant="contained">
                Save Preferences
              </Button>
            </Box>
          </Box>
        </TabPanel>

        {/* Automation Tab */}
        <TabPanel value={tabValue} index={3}>
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Automation Rules
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenAutomationDialog(true)}
              >
                Add Rule
              </Button>
            </Box>
            <List>
              {automationRules.map((rule) => (
                <React.Fragment key={rule.id}>
                  <ListItem>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {rule.name}
                          {rule.isActive && <Chip label="Active" color="success" size="small" />}
                        </Box>
                      }
                      secondary={rule.description}
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        edge="end"
                        checked={rule.isActive}
                        onChange={() => handleToggleAutomationRule(rule.id, rule.isActive)}
                      />
                      <IconButton edge="end" color="error">
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
              {automationRules.length === 0 && (
                <ListItem>
                  <ListItemText
                    primary="No automation rules yet"
                    secondary="Create your first automation rule to streamline your workflow"
                  />
                </ListItem>
              )}
            </List>
          </Box>
        </TabPanel>
      </Paper>

      {/* Create Automation Rule Dialog */}
      <Dialog open={openAutomationDialog} onClose={() => setOpenAutomationDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Automation Rule</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Rule Name"
                value={newRuleName}
                onChange={(e) => setNewRuleName(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Trigger Event"
                select
                value={newRuleTrigger}
                onChange={(e) => setNewRuleTrigger(e.target.value)}
              >
                <MenuItem value="task_created">Task Created</MenuItem>
                <MenuItem value="task_updated">Task Updated</MenuItem>
                <MenuItem value="task_completed">Task Completed</MenuItem>
                <MenuItem value="task_assigned">Task Assigned</MenuItem>
                <MenuItem value="comment_added">Comment Added</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Action"
                select
                value={newRuleAction}
                onChange={(e) => setNewRuleAction(e.target.value)}
              >
                <MenuItem value="send_notification">Send Notification</MenuItem>
                <MenuItem value="assign_user">Assign User</MenuItem>
                <MenuItem value="update_status">Update Status</MenuItem>
                <MenuItem value="add_label">Add Label</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAutomationDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateAutomationRule}
            variant="contained"
            disabled={!newRuleName}
          >
            Create Rule
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SettingsPage;
